export const collectionChecklist = [
  'Advanced musculoskeletal assessment',
  'Complex differential diagnosis cases',
  'Fractures, dislocations & combined injuries',
  'Complex conditions & special populations',
  'Surgical vs. nonsurgical treatment tools',
  'Complications, revision & follow-up',
  '40 musculoskeletal assessment forms',
  '40 imaging & testing reference matrices',
  '40 surgical safety checklists',
  '40 clinical reporting templates',
  '40 patient instruction handouts',
];

export const problemCards = [
  {
    variant: 'blue' as const,
    title: 'What if your essential orthopedic resources were already organized?',
    paragraphs: ['Complex clinical situations rarely fit into a single textbook explanation:'],
    list: [
      'Symptoms affecting multiple anatomical regions.',
      "Imaging findings that don't match the examination.",
      'Fractures with associated injuries.',
      'Conditions complicated by diabetes, osteoporosis, or previous surgery.',
    ],
    outro: [
      'Instead of relying on scattered materials, Complete Orthopedic & Trauma Toolkit™ brings together six essential areas of orthopedic and trauma practice, plus five collections of professional tools.',
    ],
    highlight: 'Assess. Analyze. Prioritize. Plan.',
  },
  {
    variant: 'gold' as const,
    title: 'Spend less time organizing clinical resources.',
    paragraphs: [
      'Your time should go to evaluating patients and coordinating care — not rebuilding the same tables, searching through files, or creating forms from scratch.',
    ],
    outro: [
      "Open the relevant volume, find the clinical situation, review the structured material, and adapt it to your patient and applicable guidelines.",
    ],
    highlight: 'Less repetitive preparation. More organized clinical resources.',
  },
  {
    variant: 'navy' as const,
    title: 'Less searching. More practical orthopedic tools.',
    paragraphs: [],
    qa: [
      { q: 'Complex musculoskeletal assessment?', a: 'Advanced clinical assessment resources.' },
      { q: 'Competing diagnoses?', a: 'Complex cases with conflicting findings and decision points.' },
      { q: 'Combined trauma injuries?', a: 'Associated injuries, neurovascular assessment, and clinical priorities.' },
      { q: 'Comparing treatment strategies?', a: 'Matrices for indications, limitations, and expected benefits.' },
      { q: 'Complex follow-up?', a: 'Complications, unfavorable recovery patterns, and reassessment.' },
    ],
    highlight: 'Open the resource. Find the information. Adapt it to your practice.',
  },
];

export const volumes = [
  {
    number: '01',
    title: 'Advanced Musculoskeletal Clinical Assessment',
    subtitle: 'Structured resources for evaluating complex musculoskeletal presentations.',
    points: [
      'Extremities, spine, pelvis, gait, and neurological function — including instability, weakness, neurovascular findings, and situations where pain or previous surgery affect examination reliability.',
    ],
    finalLine: 'Examine. Interpret. Document. Reassess.',
    meta: '40 Pages of Advanced Clinical Resources',
  },
  {
    number: '02',
    title: 'Advanced Differential Diagnosis & Complex Clinical Cases',
    subtitle: 'Cases involving conflicting findings and diagnostic uncertainty.',
    points: [
      'Complex fictional cases from shoulder to foot, with competing hypotheses, relevant positive and negative findings, and presentations requiring urgent exclusion of serious conditions.',
    ],
    finalLine: 'Compare. Question. Interpret. Clarify.',
    meta: '40 Pages of Advanced Clinical Resources',
  },
  {
    number: '03',
    title: 'Complex Trauma, Fractures & Combined Injuries',
    subtitle: 'Understand complex injury patterns and associated clinical risks.',
    points: [
      'Pelvic, acetabular, spinal, lower-limb, pediatric, and multiple injuries — with injury-pattern tools and prioritization tables. Designed to complement trauma protocols, not replace them.',
    ],
    finalLine: 'Recognize. Prioritize. Assess. Escalate.',
    meta: '40 Pages of Advanced Clinical Resources',
  },
  {
    number: '04',
    title: 'Complex Orthopedic Conditions & Special Populations',
    subtitle: 'Established conditions with functional limitations and patient-specific risks.',
    points: [
      'Degenerative, tendon, spinal, and metabolic bone disorders in patients with osteoporosis, diabetes, frailty, and other comorbidities — plus pediatric, adolescent, and athlete resources.',
    ],
    finalLine: 'Assess. Stratify. Coordinate. Monitor.',
    meta: '40 Pages of Advanced Clinical Resources',
  },
  {
    number: '05',
    title: 'Advanced Treatment Decisions & Clinical Planning',
    subtitle: 'Compare treatment alternatives and organize complex decisions.',
    points: [
      'Comparison matrices for surgical and nonsurgical options — indications, limitations, expected benefits, and uncertainties — supporting evidence-informed, individualized planning.',
    ],
    finalLine: 'Compare. Evaluate. Plan. Decide.',
    meta: '40 Pages of Advanced Clinical Resources',
  },
  {
    number: '06',
    title: 'Complications, Revision Scenarios & Advanced Follow-Up',
    subtitle: 'Evaluate difficult recovery patterns and complex follow-up situations.',
    points: [
      'Recognize warning signs, investigate unsuccessful treatment, consider revision procedures, and organize clinical reassessment with structured follow-up frameworks.',
    ],
    finalLine: 'Reassess. Investigate. Coordinate. Follow up.',
    meta: '40 Pages of Advanced Clinical Resources',
  },
];

// Pages shown in the "Take a look inside" carousel, in order 1–6.
export const insidePages = [
  {
    image: 'https://cdn.magicpatterns.com/uploads/ictKN6PyZVo8gpurqHsEJ7/1_T.png',
    alt: 'Adapting examination when pain limits reliability — examination under severe pain or muscle guarding.',
  },
  {
    image: 'https://cdn.magicpatterns.com/uploads/qrQBjVBARFiHEUj1eyUcad/2_T.png',
    alt: 'Examining functional loss beyond isolated range of motion — advanced objective functional baseline.',
  },
  {
    image: 'https://cdn.magicpatterns.com/uploads/2wQgKuXQFtCoXZAFeU99r8/3_T.png',
    alt: 'Advanced shoulder movement differentiation — capsular, contractile and functional examination.',
  },
  {
    image: 'https://cdn.magicpatterns.com/uploads/nobZpG4Venwe1KHbh6giCa/4_T.png',
    alt: 'Localising complex hand function deficits — hand function, tendon and intrinsic muscle examination.',
  },
  {
    image: 'https://cdn.magicpatterns.com/uploads/xdM5FSrwSW4rzk2oecstWb/5_T.png',
    alt: 'Assessing hip function when standard testing is limited — hip examination in the older or frail patient.',
  },
  {
    image: 'https://cdn.magicpatterns.com/uploads/vRkpnmrHF9raHwPdp4cmpj/6_T.png',
    alt: 'Examining multiple potential contributors to athletic groin symptoms — athletic groin and hip-pelvis assessment.',
  },
];

export const insideCategories = [
  { title: 'Advanced Assessment', text: 'Examination findings and interpretation tools.' },
  { title: 'Diagnostic Reasoning', text: 'Competing hypotheses and decision points.' },
  { title: 'Fracture & Trauma', text: 'Diagrams, associated injuries, and priorities.' },
  { title: 'Complex Conditions', text: 'Comorbidities and special populations.' },
  { title: 'Treatment Matrices', text: 'Strategies, risk factors, and planning.' },
  { title: 'Documentation', text: 'Forms, reports, checklists, and handouts.' },
];

export const bonuses = [
  {
    number: '01',
    title: '40 Advanced Musculoskeletal Assessment Forms',
    subtitle: 'Organize and document complex musculoskeletal findings.',
    price: 12.9,
    intro: [
      'Forms for initial assessments, multi-region symptoms, spine, neurological findings, gait, and functional limitations — with space for laterality, measurements, and examination limitations.',
    ],
    finalLine: 'Assess. Record. Organize. Review.',
  },
  {
    number: '02',
    title: '40 Diagnostic Imaging & Testing Reference Matrices',
    subtitle: 'Review investigation options and their clinical limitations.',
    price: 10.9,
    intro: ['Roles and limitations of X-rays, CT, MRI, ultrasound, and other relevant investigations.'],
    finalLine: 'Compare. Evaluate. Verify. Interpret.',
  },
  {
    number: '03',
    title: 'Ortho Surgical Safety Pro™',
    collection: '40 Preoperative & Perioperative Safety Checklists',
    subtitle: 'Organize surgical safety information and team responsibilities.',
    price: 15.9,
    intro: [
      'Patient and procedure verification, imaging review, equipment, and risk factors. Complements institutional protocols — does not replace them.',
    ],
    finalLine: 'Verify. Prepare. Coordinate. Confirm.',
  },
  {
    number: '04',
    title: 'Ortho Clinical Reports Pro™',
    collection: '40 Documentation & Reporting Templates',
    subtitle: 'Reports, referrals, consultations, and complex documentation.',
    price: 9.9,
    intro: [
      'Consultation reports, specialist referrals, second opinions, trauma transfers, and treatment decision records.',
    ],
    finalLine: 'Document. Communicate. Clarify. Organize.',
  },
  {
    number: '05',
    title: 'Ortho Recovery Ready™',
    collection: '40 Patient Instruction & Follow-Up Handouts',
    subtitle: 'Personalizable materials for every stage of patient care.',
    price: 12.9,
    intro: [
      'Activity precautions, device care, symptom monitoring, and restrictions — with space for instructions approved by the treating professional.',
    ],
    finalLine: 'Personalize. Explain. Share. Support.',
  },
];

export const benefits = [
  { title: 'Advanced clinical content', text: 'Complex situations — not elementary explanations.' },
  { title: 'Resources for each clinical need', text: 'From assessment and diagnosis to treatment and follow-up.' },
  { title: 'Comprehensive anatomical coverage', text: 'Extremities, spine, pelvis, and multi-region conditions.' },
  { title: 'Professional forms & templates', text: 'Assessments, reports, investigations, and patient communication.' },
  { title: 'Printable & reusable', text: 'Print what you need, within the license terms.' },
  { title: 'One organized collection', text: 'Volumes, forms, matrices, and patient materials together.' },
  { title: 'Less repetitive preparation', text: 'Adapt ready resources instead of rebuilding them.' },
];

export const offerItems = [
  '6 advanced clinical volumes',
  '5 FREE professional bonuses',
  'Assessment, diagnosis & complex trauma',
  'Conditions, treatment planning & follow-up',
  '200 forms, matrices, checklists, templates & handouts',
];

export const audiences = [
  { title: 'Orthopedic surgeons', text: 'Assessment, treatment considerations, documentation, and follow-up.' },
  { title: 'Trauma specialists', text: 'Complex fractures, associated injuries, and clinical priorities.' },
  { title: 'Hospital-based physicians', text: 'Consultations, patient transfers, and multidisciplinary discussions.' },
  { title: 'Advanced residents', text: 'Specialist-level scenarios that complement supervised training.' },
  { title: 'Multidisciplinary teams', text: 'Communication and patient instruction resources for coordinated care.' },
];

export const testimonials = [
  {
    name: 'Dr. Richard M.',
    age: 56,
    role: 'Orthopedic surgeon · Texas, USA',
    quote:
      "After 25 years in practice I don't need basics — I need things organized. The treatment decision matrices let me lay out indications and uncertainties on one page before a consultation.",
  },
  {
    name: 'Dr. Elena V.',
    age: 48,
    role: 'Orthopedic trauma surgeon · Madrid, Spain',
    quote:
      'I review the combined-injuries volume with our residents for high-energy cases. It complements our protocols instead of competing with them, and the transfer templates are now in our team folder.',
  },
  {
    name: 'Dr. Andrew T.',
    age: 42,
    role: 'Hospital-based orthopedic physician · Ontario, Canada',
    quote:
      'I used to keep forms in four different folders. Now assessment forms, imaging matrices, and patient handouts live in one collection.',
  },
];

export const faqs = [
  { q: 'Is this a physical product?', a: 'No. It is a 100% digital PDF collection. Nothing will be shipped.' },
  {
    q: 'Who is it designed for?',
    a: 'Orthopedic surgeons, trauma specialists, hospital-based orthopedic physicians, and trained musculoskeletal professionals. It assumes advanced clinical knowledge.',
  },
  {
    q: 'Is this a course or certification?',
    a: 'No. It is a professional reference collection — not a course, accredited CME program, or certification.',
  },
  { q: 'Is it a beginner book?', a: 'No. It is organized around advanced clinical problems and complex presentations.' },
  {
    q: 'Do I have to create the forms myself?',
    a: 'No. Forms, matrices, checklists, templates, and handouts are prestructured and ready to adapt.',
  },
  {
    q: 'What areas does it cover?',
    a: 'Shoulder, elbow, wrist, hand, spine, pelvis, hip, knee, ankle, foot, complex trauma, and selected pediatric and special-population conditions.',
  },
  {
    q: 'Does it include surgical and nonsurgical treatment?',
    a: 'Yes. It does not replace surgical training or institutional protocols.',
  },
  { q: 'Can I print and adapt the materials?', a: 'Yes, within the license terms and applicable requirements.' },
  {
    q: 'Do these resources replace medical judgment?',
    a: 'No. Patient handouts must be reviewed and approved by the treating professional before use.',
  },
  {
    q: 'How will I receive my purchase?',
    a: "You'll receive access instructions right after purchase. Support: compradigitalyaa@gmail.com.",
  },
];

export const recentBuyers = [
  { name: 'Dr. Daniel Hoffmann', place: 'Hamburg, Germany', minutes: 3 },
  { name: 'Dr. Laura Bianchi', place: 'Milan, Italy', minutes: 7 },
  { name: 'Dr. Ahmed Al-Rashid', place: 'Dubai, UAE', minutes: 5 },
  { name: 'Dr. Carlos Mendoza', place: 'Bogotá, Colombia', minutes: 12 },
  { name: 'Dr. Emily Carter', place: 'Boston, United States', minutes: 4 },
  { name: 'Dr. Kenji Watanabe', place: 'Osaka, Japan', minutes: 9 },
  { name: 'Dr. Sophie Martin', place: 'Lyon, France', minutes: 14 },
  { name: 'Dr. Rafael Souza', place: 'São Paulo, Brazil', minutes: 6 },
  { name: 'Dr. Thomas Reid', place: 'Edinburgh, United Kingdom', minutes: 11 },
  { name: 'Dr. Priya Nair', place: 'Melbourne, Australia', minutes: 8 },
];
